import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { Download, Search } from "lucide-react";
import WinnersTable from "components/winners/WinnersTable";
import { downloadWinnersCsv } from "services/apiwinners";
import { useMerchantWinners } from "../winners/useMerchantWinners";

type WinnerPerGameSlotProps = {
  gameSlotId?: string;
};

const WinnerPerGameSlot = ({ gameSlotId: gameSlotIdProp }: WinnerPerGameSlotProps) => {
  const { gameSlotId: gameSlotIdParam } = useParams<{ gameSlotId: string }>();
  const gameSlotId = gameSlotIdProp ?? gameSlotIdParam;
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const parsedSlotId =
    gameSlotId && !Number.isNaN(Number(gameSlotId)) ? Number(gameSlotId) : undefined;

  const filters = {
    search: debouncedSearch,
    gameSlotId: parsedSlotId,
  };

  const size = 20;
  const {
    isLoading,
    winners,
    isFetching,
    hasNextPage,
    hasPreviousPage,
    goToNextPage,
    goToPreviousPage,
    totalPages,
    currentPage,
    totalElements,
    resetPage,
    error,
  } = useMerchantWinners(size, filters);

  useEffect(() => {
    resetPage();
  }, [debouncedSearch, parsedSlotId, resetPage]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadWinnersCsv(filters);
      toast.success("Slot winners report downloaded");
    } catch (downloadError) {
      toast.error(
        downloadError instanceof Error
          ? downloadError.message
          : "Could not download winners report"
      );
    } finally {
      setDownloading(false);
    }
  };

  if (!parsedSlotId) {
    return (
      <div className="noresult my-5">
        <h6>Missing game slot id — cannot load winners.</h6>
      </div>
    );
  }

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} limit={1} />

      <div className="card">
        <div className="card-body">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-4">
            <div className="relative flex-1 max-w-xl">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                type="text"
                className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 w-full dark:bg-zink-700 dark:text-zink-100"
                placeholder="Search by name, phone, or ticket number"
                autoComplete="off"
              />
              <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200" />
            </div>
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-custom-500 text-white hover:bg-custom-600 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {downloading ? "Downloading..." : "Download CSV"}
            </button>
          </div>

          <p className="text-sm text-slate-500 dark:text-zink-300 mb-2">
            {totalElements.toLocaleString()} winner(s) for slot #{parsedSlotId}
          </p>

          {error ? (
            <div className="noresult my-5 text-red-500">
              <h6>Could not load winners</h6>
              <p className="text-sm mt-1">{String(error)}</p>
            </div>
          ) : (
            <WinnersTable
              winners={winners}
              isLoading={isLoading}
              isFetching={isFetching}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              goToNextPage={goToNextPage}
              goToPreviousPage={goToPreviousPage}
              currentPage={currentPage}
              totalPages={totalPages}
              showSlotColumns={false}
              showActions={false}
            />
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default WinnerPerGameSlot;
